import { FC, useEffect } from 'react'
import useChartStore from '../../store/chart/useChartStore'

import { useQuery } from '@tanstack/react-query'
import { getAverageWeightChart } from '@/api/chart/chart.service'
import { useColorBySchool } from '../../hooks/chart/useColorBySchool'
import { useAvgDistDateStore } from '../../store/chart/useAvgDistDateStore'
import { AverageWeightChart } from './AverageWeightChart'
import { ChartComponent } from './ChartComponent'
import { EmptyChartState } from './EmptyChartState'
import { HistComponent } from './HistComponent'
import { WaterQualityChartComponent } from './WaterQualityChartComponent'

export type ChartContainerProps = {
  showState: boolean
}

export const ChartContainer: FC<ChartContainerProps> = ({ showState }) => {
  const { properties, selectedSchools } = useChartStore()
  const { colorForEachSchool, setColoredSchools } = useColorBySchool(selectedSchools)
  const { date } = useAvgDistDateStore()

  useEffect(() => {
    setColoredSchools(selectedSchools)
  }, [selectedSchools, setColoredSchools])

  // Check Average Weight data availability
  const averageWeightQuery = useQuery({
    queryKey: ['chart', ['averageWeight'], selectedSchools],
    queryFn: async () => {
      if (selectedSchools.length === 0) return null
      return await getAverageWeightChart(selectedSchools)
    },
    staleTime: 3600,
    refetchOnMount: false,
  })

  // Filter out averageWeight and nh3no2 from properties for separate handling
  const otherProperties = properties.filter(
    (prop) => prop !== 'averageWeight' && prop !== 'nh3no2'
  )
  const hasAverageWeight = properties.includes('averageWeight')
  const hasNh3No2 = properties.includes('nh3no2')
  const hasAverageWeightData =
    averageWeightQuery.data &&
    Object.keys(averageWeightQuery.data.xyPointsPerSchoolId).length > 0

  return (
    <div className="mt-[2rem]">
      {/* First row - Average Weight and Weight Histogram */}
      {showState && selectedSchools.length > 0 && hasAverageWeight && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[2rem] mb-[2rem]">
          <AverageWeightChart schoolColors={colorForEachSchool} />
          {!hasAverageWeightData ? (
            <div className="flex flex-col">
              <div className="mx-1 mb-1 flex flex-row justify-between">
                <p className="font-bold text-[1.25rem]">Weight Histogram</p>
              </div>
              <EmptyChartState
                title="Weight Histogram"
                message="No data available for this property"
              />
            </div>
          ) : date ? (
            <HistComponent date={date} schoolColors={colorForEachSchool} />
          ) : (
            <div className="flex flex-col">
              <div className="mx-1 mb-1 flex flex-row justify-between">
                <p className="font-bold text-[1.25rem]">Weight Histogram</p>
              </div>
              <div className="h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] bg-white border border-gray-200 rounded-lg flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <div className="text-sm font-medium">Weight Histogram</div>
                  <div className="text-xs mt-1">Select date to view chart</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Other charts (excluding averageWeight and nh3no2) */}
      {showState && selectedSchools.length > 0 && otherProperties.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[2rem] mb-[2rem]">
          {otherProperties.map((property, index) => (
            <ChartComponent key={index} property={property} schoolColors={colorForEachSchool} />
          ))}
        </div>
      )}

      {/* Water Quality charts (NH3 & NO2) */}
      {showState && selectedSchools.length > 0 && hasNh3No2 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[2rem]">
          <WaterQualityChartComponent schoolColors={colorForEachSchool} />
        </div>
      )}
    </div>
  )
}
